'use client';

import classNames from 'classnames';
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const PAD = 8;

type Placement = 'left' | 'right' | 'top' | 'bottom';

function computePosition(
  anchorRect: DOMRect,
  tipRect: DOMRect,
  placement: Placement,
): { left: number; top: number } {
  const gap = 8;
  switch (placement) {
    case 'right':
      return {
        left: anchorRect.right + gap,
        top: anchorRect.top + anchorRect.height / 2 - tipRect.height / 2,
      };
    case 'left':
      return {
        left: anchorRect.left - tipRect.width - gap,
        top: anchorRect.top + anchorRect.height / 2 - tipRect.height / 2,
      };
    case 'top':
      return {
        left: anchorRect.left + anchorRect.width / 2 - tipRect.width / 2,
        top: anchorRect.top - tipRect.height - gap,
      };
    case 'bottom':
    default:
      return {
        left: anchorRect.left + anchorRect.width / 2 - tipRect.width / 2,
        top: anchorRect.bottom + gap,
      };
  }
}

function clampToViewport(
  left: number,
  top: number,
  width: number,
  height: number,
): { left: number; top: number } {
  return {
    left: Math.max(PAD, Math.min(left, window.innerWidth - width - PAD)),
    top: Math.max(PAD, Math.min(top, window.innerHeight - height - PAD)),
  };
}

function getBestPlacement(placement: Placement, anchorRect: DOMRect, tipRect: DOMRect): Placement {
  const order: Placement[] = [
    placement,
    ...(placement === 'left' || placement === 'right'
      ? (['top', 'bottom'] as Placement[])
      : (['left', 'right'] as Placement[])),
    ...(placement === 'left'
      ? (['right'] as Placement[])
      : placement === 'right'
        ? (['left'] as Placement[])
        : placement === 'top'
          ? (['bottom'] as Placement[])
          : (['top'] as Placement[])),
  ];

  let best: Placement = placement;
  let bestScore = Number.POSITIVE_INFINITY;

  order.forEach((candidate) => {
    const pos = computePosition(anchorRect, tipRect, candidate);
    const overflowLeft = Math.max(0, PAD - pos.left);
    const overflowRight = Math.max(0, pos.left + tipRect.width - (window.innerWidth - PAD));
    const overflowTop = Math.max(0, PAD - pos.top);
    const overflowBottom = Math.max(0, pos.top + tipRect.height - (window.innerHeight - PAD));
    const score = overflowLeft + overflowRight + overflowTop + overflowBottom;

    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  });

  return best;
}

export default function Tooltip({
  message,
  placement = 'right',
  theme = 'dark',
  maxWidth,
  children,
}: {
  message: ReactNode;
  placement?: Placement;
  theme?: 'dark' | 'light';
  maxWidth?: number;
  children: ReactNode;
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  const themeClass =
    theme === 'dark'
      ? 'tw:bg-cn-dark-blue tw:text-white tw:border tw:border-gray-300'
      : 'tw:bg-white tw:text-cn-dark-blue tw:border tw:border-gray-300';

  useLayoutEffect(() => {
    if (!open || typeof document === 'undefined') return;

    const anchorEl = anchorRef.current;
    const tipEl = tipRef.current;
    if (!anchorEl || !tipEl) return;

    let cancelled = false;

    const updatePosition = () => {
      if (cancelled) return;
      const anchorRect = anchorEl.getBoundingClientRect();
      const tipRect = tipEl.getBoundingClientRect();

      const bestPlacement = getBestPlacement(placement, anchorRect, tipRect);
      const { left, top } = computePosition(anchorRect, tipRect, bestPlacement);
      const clamped = clampToViewport(left, top, tipRect.width, tipRect.height);
      setPosition(clamped);
    };

    // Initial position (may need flip)
    updatePosition();

    // Re-run after layout settles (handles placement flip)
    const rafId = requestAnimationFrame(updatePosition);

    const handleScrollOrResize = () => requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, placement, message]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      const anchorEl = anchorRef.current;
      const tipEl = tipRef.current;
      const insideAnchor = !!anchorEl && anchorEl.contains(target);
      const insideTooltip = !!tipEl && tipEl.contains(target);

      if (!insideAnchor && !insideTooltip) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const tooltipContent = (
    <div
      ref={tipRef}
      className={classNames(
        themeClass,
        'tw:w-max',
        'tw:max-w-[min(90vw,400px)] tw:break-words tw:whitespace-normal',
        'tw:z-[9999] tw:fixed tw:rounded tw:text-xs tw:p-2 tw:shadow-lg',
        'tw:transition-[opacity,transform] tw:duration-150',
        open ? 'tw:opacity-100 tw:scale-100' : 'tw:opacity-0 tw:scale-95 tw:pointer-events-none',
      )}
      style={{
        maxWidth: maxWidth ?? undefined,
        left: position?.left ?? -9999,
        top: position?.top ?? -9999,
      }}
    >
      {message}
    </div>
  );

  return (
    <>
      <div
        ref={anchorRef}
        className="tw:inline-flex tw:items-center tw:justify-center"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // A tap can focus first and then click on mobile; always opening here avoids a blink.
        onClick={() => setOpen(true)}
      >
        {children}
      </div>
      {/* Portal subtree is cleaned up by React when closed or when Tooltip unmounts. */}
      {open && typeof document !== 'undefined' ? createPortal(tooltipContent, document.body) : null}
    </>
  );
}
